/* eslint-disable max-lines-per-function */
import React from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import { LoadingSpinner, ServerError } from "components/graphql-views";
import { updatePlan } from "types/gql-types";
import { AdminHeading } from "../shared/admin-form";
import { useAdminSubmit } from "../shared/use-admin-submit";
import PlanForm, { PlanForm as PlanFormType } from "./form";
import { buildPlanMutationData } from "./plan-form-utils";
import { UPDATE_PLAN, GET_PLAN_BY_ID, GET_PRICING_ALL_FOR_PLANS } from "./queries";
import { formatPlanPricingOptions, NoPricingWarning } from "./plan-utils";

const UpdatePlan: React.FC = () => {
  const { planId } = useParams<{ planId: string }>();
  const { sending, submit } = useAdminSubmit("/dash/admin/plans");
  const [updatePlanFunc] = useMutation<updatePlan>(UPDATE_PLAN);

  const { loading, error, data: planData } = useQuery(GET_PLAN_BY_ID, {
    variables: { id: planId },
    skip: !planId,
  });

  const { data: pricingData } = useQuery(GET_PRICING_ALL_FOR_PLANS, {
    fetchPolicy: "cache-and-network",
  });

  const submitForm = (formData: PlanFormType) => {
    if (!planId) return;

    submit(() =>
      updatePlanFunc({
        variables: {
          id: planId,
          data: buildPlanMutationData(formData),
        },
        refetchQueries: ["plansAll"],
      }));
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <ServerError desc={error.message} />;
  if (!planData?.plan) return <div>Plan not found</div>;

  const { plan } = planData;
  const pricingOptions = formatPlanPricingOptions(pricingData?.pricingAll);

  const initialData: PlanFormType = {
    name: plan.name || "",
    description: plan.description || "",
    isPublic: plan.isPublic || false,
    defaultPricingId: plan.defaultPricingId || "",
    invoicedPlanIdAnnually: plan.invoicedPlanIds?.annually || "",
    invoicedPlanIdQuarterly: plan.invoicedPlanIds?.quarterly || "",
  };

  return (
    <div>
      <AdminHeading>Update Subscription Plan</AdminHeading>
      {pricingOptions.length === 0 && <NoPricingWarning />}
      <PlanForm
        formType="update"
        initialData={initialData}
        sending={sending}
        handleSubmit={submitForm}
        pricingOptions={pricingOptions}
      />
    </div>
  );
};

export default UpdatePlan;
